// CRM 2.0 cancellation pop-ups (M7-F6 / F7 / F8; rows M7.16 / M7.18 / M7.22 / M7.23 / M7.27, DC-97 / DC-144),
// prototype cxRaise / cxApproveDraw / cxReject / cxExtend / Reprice Unit. Booking stage only (no charges, DC-121):
// Agreement-stage charges come with M7-F10. Every action is server side (routes/crm2.js /cancellations/*).
import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import Button from "../../../Subcomponents/Button";
import { crm2CancelDecide, crm2CancelExtend, crm2CancelHeadDecide, crm2CancelPayments, crm2CancelRaise, crm2CancelReasons, crm2Reprice } from "../../../Services/Crm2List";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import { errorTost, successTost } from "../../../Services/Toast";
import { unitLine } from "../ui/cardParts";
import settingsCss from "../Settings/Crm2Settings.module.css";

const inr = (n) => `₹ ${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const day = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const btnSec = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem" };
const btnPri = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem" };
const btnDanger = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#D92D20", border: "1px solid #D92D20", fontSize: "1.4rem", lineHeight: "2rem" };
const lbl = { display: "block", fontSize: "1.4rem", fontWeight: 500, color: "#344054", margin: "1.4rem 0 .6rem" };
const hint = { fontSize: "1.3rem", color: "#667085" };
const input = { width: "100%", fontSize: "1.5rem", padding: "1rem", border: "1px solid #D0D5DD", borderRadius: ".8rem", boxSizing: "border-box", background: "#FFFFFF" };
const msgOf = (e, fallback) => e?.response?.data?.message || fallback;

function Shell({ title, onClose, wide = false, children, footer }) {
  return (
    <Modal open onClose={onClose} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={settingsCss.modalWrapper} style={{ width: wide ? "76rem" : "56rem", maxWidth: "calc(100% - 3.2rem)" }}>
        <div className={settingsCss.modalHeader}>
          <span>{title}</span>
          <span className={settingsCss.modalClose} onClick={onClose} tabIndex={0} role="button" aria-label="Close">
            <Close width="1.2rem" height="1.2rem" />
          </span>
        </div>
        <div className={settingsCss.modalContent} style={{ maxHeight: "70vh", overflow: "auto" }}>
          {children}
        </div>
        <div className={settingsCss.modalFooter}>{footer}</div>
      </Box>
    </Modal>
  );
}

async function run(fn, ok, fail, onDone) {
  presentLoader();
  try {
    const r = await fn();
    successTost(typeof ok === "function" ? ok(r) : ok);
    onDone();
  } catch (e) {
    console.error("[crm2][cancellation] action failed", { status: e?.response?.status, message: e?.response?.data?.message });
    errorTost(msgOf(e, fail));
  } finally {
    dismissLoader();
  }
}

// ⋮ Cancel Unit → Raise Cancellation (CRM raised; the customer confirms by OTP)
export function RaiseCancellationModal({ card, onClose, onDone }) {
  const [reasons, setReasons] = useState([]);
  const [code, setCode] = useState("");
  const [details, setDetails] = useState("");
  useEffect(() => {
    crm2CancelReasons("CANCEL_RAISE")
      .then((d) => setReasons(d?.reasons || []))
      .catch((e) => errorTost(msgOf(e, "Could not load the reasons")));
  }, []);
  const submit = () => {
    if (!code) return errorTost("Select the reason");
    if (!details.trim()) return errorTost("Enter the details");
    run(() => crm2CancelRaise(card.allotmentPaymentId, { reasonCode: code, details: details.trim() }), "Cancellation raised. Consent link sent to the customer", "Could not raise the cancellation", onDone);
  };
  return (
    <Shell
      title="Raise Cancellation"
      wide
      onClose={onClose}
      footer={
        <>
          <Button btnName="Cancel" style={btnSec} clicked={onClose} />
          <Button btnName="Raise and send consent link" style={btnPri} clicked={submit} />
        </>
      }
    >
      <p style={{ ...hint, marginTop: 0 }}>
        {unitLine(card)} · {card.leadName} · Application for Booking
      </p>
      <label style={lbl}>
        Reason <sup style={{ color: "red" }}>*</sup>
      </label>
      <select value={code} onChange={(e) => setCode(e.target.value)} style={input}>
        <option value="">Select</option>
        {reasons.map((r) => (
          <option key={r.code} value={r.code}>
            {r.label}
          </option>
        ))}
      </select>
      <label style={lbl}>
        Details <sup style={{ color: "red" }}>*</sup>
      </label>
      <textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Calls, emails, what happened" style={{ ...input, minHeight: "8rem" }} />
      <div style={{ background: "#FFFAEB", border: "1px solid #FEDF89", color: "#B54708", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.35rem", marginTop: "1.6rem" }}>
        Customer consent is needed. A consent link goes to every applicant by email and WhatsApp; the customer picks an applicant and the OTP goes to that applicant's mobile (Customer Portal). Then the CRM Head approves and the full refund (no charges) starts.
      </div>
    </Shell>
  );
}

// Your approval / Your decision → Approve Cancellation (payments, refund split, refund handling)
export function ApproveCancellationModal({ card, onClose, onDone }) {
  const x = card.cancel || {};
  const [p, setP] = useState(null);
  const [mode, setMode] = useState("REFUND");
  const [reason, setReason] = useState("");
  useEffect(() => {
    crm2CancelPayments(x.cancelUnitRequestId)
      .then(setP)
      .catch((e) => {
        errorTost(msgOf(e, "Could not open this cancellation"));
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x.cancelUnitRequestId]);
  const go = (m) => {
    if ((m === "HOLD" || m === "NO_REFUND") && !reason.trim()) return errorTost("Enter the reason");
    const ok = m === "NO_REFUND" ? "Accepted without refund. Sent to the CRM Head" : m === "HOLD" ? "Approved with refund on hold. Sent to the CRM Head" : "Approved. Sent to the CRM Head";
    run(() => crm2CancelDecide(x.cancelUnitRequestId, { action: "approve", mode: m, reason: reason.trim() }), ok, "Could not approve the cancellation", onDone);
  };
  const auto = /^OVERDUE/.test(x.source || "");
  return (
    <Shell
      title="Approve Cancellation"
      wide
      onClose={onClose}
      footer={
        <>
          <Button btnName="Cancel" style={btnSec} clicked={onClose} />
          <Button btnName="Accept without Refund" style={btnSec} clicked={() => go("NO_REFUND")} />
          <Button btnName="Approve" style={btnPri} clicked={() => go(mode)} />
        </>
      }
    >
      <p style={{ fontSize: "1.5rem", fontWeight: 600, color: "#101828", margin: 0 }}>
        {unitLine(card)} · {card.leadName}
      </p>
      <p style={{ ...hint, color: auto ? "#B42318" : "#667085" }}>{auto ? `Not done: ${(x.notDoneSteps || []).join(", ")}` : x.reason}</p>
      {!p ? (
        <p style={hint}>Loading…</p>
      ) : (
        <>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "1.35rem", marginTop: "1.2rem" }}>
            <thead>
              <tr style={{ background: "#F9FAFB", color: "#475467" }}>
                {["Date", "Payment", "Mode", "Amount"].map((h) => (
                  <th key={h} style={{ textAlign: h === "Amount" ? "right" : "left", padding: ".6rem .8rem" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {p.payments.map((r) => (
                <tr key={r.transactionId} style={{ borderTop: "1px solid #EAECF0" }}>
                  <td style={{ padding: ".6rem .8rem" }}>
                    {day(r.at)} {r.over6Months && <span style={{ fontSize: "1.1rem", color: "#B54708", background: "#FFFAEB", borderRadius: "1.6rem", padding: "0 .6rem" }}>over 6 months</span>}
                  </td>
                  <td style={{ padding: ".6rem .8rem" }}>{r.parts.sdr >= r.amount ? "Stamp duty & charges (SDR)" : r.parts.sdr ? "Booking amount + SDR" : "Booking amount + OCR"}</td>
                  <td style={{ padding: ".6rem .8rem" }}>{r.mode}</td>
                  <td style={{ padding: ".6rem .8rem", textAlign: "right" }}>{inr(r.amount)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid #EAECF0", fontWeight: 600 }}>
                <td colSpan={3} style={{ padding: ".6rem .8rem" }}>
                  Total paid
                </td>
                <td style={{ padding: ".6rem .8rem", textAlign: "right" }}>{inr(p.totalPaid)}</td>
              </tr>
            </tbody>
          </table>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "1.35rem", marginTop: "1.2rem" }}>
            <tbody>
              {p.refund.razorpay > 0 && (
                <tr>
                  <td style={{ padding: ".4rem .8rem" }}>Refund to customer</td>
                  <td style={{ padding: ".4rem .8rem", color: "#667085" }}>Razorpay, original payment method</td>
                  <td style={{ padding: ".4rem .8rem", textAlign: "right" }}>{inr(p.refund.razorpay)}</td>
                </tr>
              )}
              {p.refund.manual > 0 && (
                <tr>
                  <td style={{ padding: ".4rem .8rem" }}>Refund to customer</td>
                  <td style={{ padding: ".4rem .8rem", color: "#667085" }}>Accounts (manual, older than 6 months)</td>
                  <td style={{ padding: ".4rem .8rem", textAlign: "right" }}>{inr(p.refund.manual)}</td>
                </tr>
              )}
              <tr style={{ fontWeight: 600, color: "#027A48" }}>
                <td colSpan={2} style={{ padding: ".4rem .8rem" }}>
                  Back to the customer
                </td>
                <td style={{ padding: ".4rem .8rem", textAlign: "right" }}>{inr(p.refund.total)}</td>
              </tr>
            </tbody>
          </table>
          <p style={hint}>Refund account: the original payment method (Razorpay; Accounts by hand for payments older than 6 months).</p>
        </>
      )}
      <label style={lbl}>Refund handling (today's Confirm Cancel Unit)</label>
      {[
        ["REFUND", "Refund as above"],
        ["HOLD", "Hold Refund"],
      ].map(([v, t]) => (
        <label key={v} style={{ display: "inline-flex", gap: ".6rem", marginRight: "2rem", fontSize: "1.4rem" }}>
          <input type="radio" name="cxm" checked={mode === v} onChange={() => setMode(v)} /> {t}
        </label>
      ))}
      <label style={{ display: "inline-flex", gap: ".6rem", fontSize: "1.4rem", opacity: 0.5 }} title="Booking stage: no charges, so nothing to waive.">
        <input type="radio" name="cxm" disabled /> Waive Off
      </label>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (needed for Hold Refund, Waive Off and Accept without Refund)" style={{ ...input, minHeight: "6rem", marginTop: "1rem" }} />
      <p style={hint}>
        Booking stage: no charges, so nothing to waive. Hold Refund: the unit is released but the refund waits until someone releases it. <b>Accept without Refund</b>: cancel and keep the amount (no refund rows).
      </p>
      <p style={hint}>After you approve: the CRM Head approves and the unit is released (Cancellation Completed). The refund follows through Razorpay / Accounts (manual).</p>
    </Shell>
  );
}

// Reject (tagged CRM on a customer request, or the CRM Head)
export function RejectCancellationModal({ card, head = false, onClose, onDone }) {
  const x = card.cancel || {};
  const [reason, setReason] = useState("");
  const submit = () => {
    if (!reason.trim()) return errorTost("Enter the rejection reason");
    run(
      () => (head ? crm2CancelHeadDecide(x.cancelUnitRequestId, { action: "reject", reason: reason.trim() }) : crm2CancelDecide(x.cancelUnitRequestId, { action: "reject", reason: reason.trim() })),
      head ? "Cancellation rejected by the CRM Head. Booking stays active" : "Cancellation rejected. Booking stays active",
      "Could not reject the cancellation",
      onDone,
    );
  };
  return (
    <Shell
      title="Enter Rejection reason message *"
      onClose={onClose}
      footer={
        <>
          <Button btnName="Cancel" style={btnSec} clicked={onClose} />
          <Button btnName="Reject Confirm" style={btnDanger} clicked={submit} />
        </>
      }
    >
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Type the reason" style={{ ...input, minHeight: "8rem" }} />
      <p style={hint}>{head ? "The booking goes back to the CRM's tab and stays active; the CRM and the customer are told." : "The booking stays active in Application for Booking; the customer is told."}</p>
    </Shell>
  );
}

// Your decision (overdue) → Extend Days
export function ExtendDaysModal({ card, onClose, onDone }) {
  const x = card.cancel || {};
  const [cfg, setCfg] = useState({ extendDayChoices: [3, 5, 7, 10], maxExtendDays: 30, extendDocumentRequired: true });
  const [reasons, setReasons] = useState([]);
  const [days, setDays] = useState(5);
  const [other, setOther] = useState("");
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  useEffect(() => {
    crm2CancelReasons("CANCEL_EXTEND")
      .then((d) => {
        setReasons(d?.reasons || []);
        if (d?.settings) setCfg(d.settings);
      })
      .catch((e) => errorTost(msgOf(e, "Could not load the reasons")));
  }, []);
  const d = other ? Math.round(Number(other)) : days;
  const valid = Number.isFinite(d) && d >= 1 && d <= cfg.maxExtendDays;
  const newDate = valid ? day(Date.now() + d * 86400000) : null;
  const submit = () => {
    if (!valid) return errorTost("Enter the extra days");
    if (!code) return errorTost("Select the reason");
    if (cfg.extendDocumentRequired && !file) return errorTost("Attach the document (required)");
    run(() => crm2CancelExtend(x.cancelUnitRequestId, { days: d, reasonCode: code, note: note.trim(), file }), `Extended by ${d} days to ${newDate}. Customer told`, "Could not extend the days", onDone);
  };
  return (
    <Shell
      title="Extend Days"
      wide
      onClose={onClose}
      footer={
        <>
          <Button btnName="Cancel" style={btnSec} clicked={onClose} />
          <Button btnName={`Extend by ${valid ? d : "–"} days`} style={{ ...btnPri, opacity: valid ? 1 : 0.5 }} disable={!valid} clicked={submit} />
        </>
      }
    >
      <p style={{ ...hint, marginTop: 0 }}>
        {unitLine(card)} · {card.leadName} · Application for Booking
      </p>
      <div style={{ background: "#FEF3F2", border: "1px solid #FDA29B", color: "#B42318", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.35rem" }}>Not done in time: {(x.notDoneSteps || []).join(" · ") || "—"}</div>
      <label style={lbl}>
        Extra days <sup style={{ color: "red" }}>*</sup>
      </label>
      <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap", alignItems: "center" }}>
        {cfg.extendDayChoices.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => {
              setDays(n);
              setOther("");
            }}
            style={{ padding: ".6rem 1.2rem", borderRadius: ".8rem", border: "1px solid #D0D5DD", fontSize: "1.4rem", cursor: "pointer", background: !other && days === n ? "#155EEF" : "#FFFFFF", color: !other && days === n ? "#FFFFFF" : "#344054" }}
          >
            {n} days
          </button>
        ))}
        <input type="number" min={1} max={cfg.maxExtendDays} value={other} onChange={(e) => setOther(e.target.value)} placeholder="days" title={`Most extra days per extension: ${cfg.maxExtendDays} (setting)`} style={{ ...input, width: "10rem" }} />
      </div>
      <div style={{ background: valid ? "#ECFDF3" : "#FEF3F2", border: `1px solid ${valid ? "#ABEFC6" : "#FDA29B"}`, color: valid ? "#05603A" : "#B42318", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.35rem", marginTop: "1.2rem" }}>
        {valid ? (
          <>
            <b>NEW DEADLINE</b> {newDate} · +{d} days from today · the customer has {d} days to finish
          </>
        ) : (
          "Pick or enter the extra days"
        )}
      </div>
      <label style={lbl}>
        Reason <sup style={{ color: "red" }}>*</sup>
      </label>
      <select value={code} onChange={(e) => setCode(e.target.value)} style={input}>
        <option value="">Select</option>
        {reasons.map((r) => (
          <option key={r.code} value={r.code}>
            {r.label}
          </option>
        ))}
      </select>
      <label style={lbl}>Note</label>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" style={input} />
      <label style={lbl}>Document {cfg.extendDocumentRequired && <sup style={{ color: "red" }}>*</sup>}</label>
      {file ? (
        <span style={{ fontSize: "1.35rem" }}>
          📎 {file.name}{" "}
          <span style={{ color: "#155EEF", cursor: "pointer" }} onClick={() => setFile(null)}>
            Remove
          </span>
        </span>
      ) : (
        <label style={{ ...btnSec, display: "inline-block", padding: ".6rem 1.2rem", borderRadius: ".8rem", cursor: "pointer" }}>
          Attach document (required)
          <input type="file" accept="application/pdf,image/jpeg,image/png" style={{ display: "none" }} onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
      )}
      <p style={hint}>Customer's request or approval mail (as today's Grant Extension)</p>
      <p style={hint}>
        The booking goes back to <b>Application for Booking</b> with the new deadline and an "Extended" chip. The customer gets the new date and the pending steps by email and WhatsApp. If the new date also passes, the auto-cancellation comes back here.
      </p>
    </Shell>
  );
}

// after the cancellation: Reprice Unit (permission Reprice Unit) — the new price applies to the next booking of this unit
export function RepriceModal({ card, onClose, onDone }) {
  const x = card.cancel || {};
  const old = card.unitCost || {};
  const [bsp, setBsp] = useState("");
  const rate = (k) => (Number(old.bsp) ? Number(old[k] || 0) / Number(old.bsp) : 0);
  const nb = Math.round(Number(bsp) || 0);
  const gst = Math.round(nb * rate("gst"));
  const sdr = Math.round(nb * rate("sdr"));
  const submit = () => {
    if (!(nb > 0)) return errorTost("Enter the new agreement value");
    run(() => crm2Reprice(x.cancelUnitRequestId, { bsp: nb, gst, sdr, total: nb + gst + sdr }), `Unit repriced to ${inr(nb + gst + sdr)}`, "Could not reprice the unit", onDone);
  };
  return (
    <Shell
      title="Reprice Unit"
      onClose={onClose}
      footer={
        <>
          <Button btnName="Cancel" style={btnSec} clicked={onClose} />
          <Button btnName="Reprice Unit" style={btnPri} clicked={submit} />
        </>
      }
    >
      <p style={{ ...hint, marginTop: 0 }}>
        {unitLine(card)} · released after the cancellation of {card.leadName}
      </p>
      <label style={lbl}>Old agreement value</label>
      <input value={inr(old.bsp)} disabled style={{ ...input, background: "#F9FAFB" }} />
      <label style={lbl}>
        New agreement value <sup style={{ color: "red" }}>*</sup>
      </label>
      <input type="number" value={bsp} onChange={(e) => setBsp(e.target.value)} style={input} />
      {nb > 0 && (
        <p style={hint}>
          GST {inr(gst)} · stamp duty & charges {inr(sdr)} (same rates as before) · total {inr(nb + gst + sdr)}
        </p>
      )}
      <p style={hint}>
        Today's Reprice Unit (permission <b>Reprice Unit</b>): the new price applies to the next booking of this unit.
      </p>
    </Shell>
  );
}
